import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUpRight, Download } from "lucide-react";
import { FadeUp } from "@/components/motion";
import FinishSwatch from "@/components/FinishSwatch";
import CategoryIcon from "@/components/CategoryIcon";
import { ProductCard } from "@/pages/Home";
import { CATEGORY_LINKS } from "@/data/products";
import { useSite, assetUrl } from "@/content";

const SITE_TITLE = "SanCub — Architectural Tapware & Bathroom Fixtures | Where Form Meets Flow";
const slug = (f) => f.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const ProductDetail = () => {
  const { id } = useParams();
  const { products } = useSite();
  const p = products.find((x) => x.id === id);
  const [finish, setFinish] = useState(p?.finishes[0]);
  const [countertop, setCountertop] = useState(p?.countertops?.[0]);
  const [view, setView] = useState("finish");

  useEffect(() => {
    setFinish(p?.finishes[0]);
    setCountertop(p?.countertops?.[0]);
    setView("finish");
    window.scrollTo(0, 0);
  }, [id]);

  const sku = p ? (p.skuBase ? `${p.skuBase}-${p.finishCodes?.[finish] || ""}` : p.sku) : null;

  useEffect(() => {
    if (!p) return;
    document.title = `${p.name} | SanCub`;
    const desc = document.querySelector('meta[name="description"]');
    const prev = desc?.content;
    if (desc) {
      desc.content = `${p.name}${sku ? ` (${sku})` : ""} — SanCub ${p.category.toLowerCase()} available in ${p.finishes.join(", ")}. 100% Australian owned & operated.`;
    }
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "product-jsonld";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      name: p.name,
      sku: sku || undefined,
      category: p.category,
      brand: { "@type": "Brand", name: "SanCub" },
      image: `https://sancub.com${p.image}`,
      url: `https://sancub.com/product/${p.id}`,
    });
    document.head.appendChild(script);
    return () => {
      document.title = SITE_TITLE;
      if (desc && prev) desc.content = prev;
      document.getElementById("product-jsonld")?.remove();
    };
  }, [p, sku]);

  if (!p) {
    return (
      <main className="mx-auto max-w-[1500px] px-6 pb-32 pt-40 text-center md:px-10" data-testid="product-not-found">
        <h1 className="font-display text-4xl font-medium tracking-tight text-ink">Product not found</h1>
        <Link to="/collections" data-testid="not-found-back" className="mt-6 inline-flex items-center gap-2 rounded-full bg-ink px-7 py-3.5 text-[13px] font-semibold text-white">
          <ArrowLeft className="h-4 w-4" /> Back to collections
        </Link>
      </main>
    );
  }

  const gallery = p.finishImages ? Object.entries(p.finishImages) : [];
  const extras = (p.finishGalleries && p.finishGalleries[finish]) || p.galleryImages || [];
  const img = view === "tech"
    ? (p.techImages && p.techImages[finish]) || p.techImage
    : view.startsWith("extra-")
      ? extras[Number(view.slice(6))]
      : (p.finishImages && p.finishImages[finish]) || p.image;
  const thumbs = [
    ...((gallery.length > 1 ? gallery : []).map(([f, src]) => ({
      key: `f-${f}`, testId: `thumb-${slug(f)}`, src, label: f,
      active: view === "finish" && finish === f,
      onClick: () => { setFinish(f); setView("finish"); },
    }))),
    ...(extras.length > 0 && gallery.length <= 1 ? [{
      key: "main", testId: "thumb-main", src: p.image, label: "Product photo",
      active: view === "finish", onClick: () => setView("finish"),
    }] : []),
    ...extras.map((src, i) => ({
      key: `e-${i}`, testId: `thumb-photo-${i + 1}`, src, label: `Photo ${i + 1}`,
      active: view === `extra-${i}`, onClick: () => setView(`extra-${i}`),
    })),
    ...(p.techImage || p.techImages ? [{
      key: "tech", testId: "thumb-dimensions", src: p.techImage || Object.values(p.techImages || {})[0], label: "Dimensions",
      active: view === "tech", onClick: () => setView("tech"),
    }] : []),
  ];
  const related = products.filter((x) => x.category === p.category && x.id !== p.id).slice(0, 3);
  const size = (p.name.match(/\d{3,4}\s*mm(?:\s*x\s*\d{2,4}\s*mm)?/i) || [])[0];
  const range = p.name.split(" ")[0];
  const catLink = CATEGORY_LINKS.find((c) => c.label === p.category);
  const specs = [
    sku && { l: "SKU", v: sku },
    { l: "Category", v: p.category },
    { l: "Range", v: range },
    ...(p.extraSpecs || []),
    !p.extraSpecs && size && { l: "Size", v: size },
  ].filter(Boolean);

  const enquiryProduct =
    p.name +
    (sku ? ` (${sku})` : "") +
    (finish ? ` in ${finish}` : "") +
    (p.countertops && countertop ? ` with ${countertop} benchtop` : "");

  return (
    <main className="mx-auto max-w-[1500px] px-6 pb-32 pt-28 md:px-10 md:pt-36" data-testid="product-page">
      <FadeUp y={14}>
        <Link
          to={catLink ? catLink.to : "/collections"}
          data-testid="product-back-link"
          className="group inline-flex items-center gap-2 text-[13px] font-semibold text-ink/50 transition-colors duration-300 hover:text-ink"
        >
          <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
          Back to {p.category.toLowerCase()}
        </Link>
      </FadeUp>

      <div className="mt-8 grid grid-cols-1 gap-10 lg:grid-cols-12 lg:gap-16">
        <div className="lg:col-span-7">
          <FadeUp y={16}>
            {p.placeholder ? (
              <div className="flex h-[420px] items-center justify-center rounded-[2rem] bg-sand shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:h-[620px]" data-testid="product-gallery">
                <div className="px-6 text-center">
                  <CategoryIcon category={p.category} className="mx-auto h-12 w-12 text-ink/25" />
                  <p className="mt-4 text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/40" data-testid="placeholder-note">
                    Photos coming soon
                  </p>
                </div>
              </div>
            ) : (
              <div className="flex h-[420px] items-center justify-center rounded-[2rem] bg-white p-10 shadow-[0_8px_30px_rgb(0,0,0,0.04)] md:h-[620px] md:p-16" data-testid="product-gallery">
                <AnimatePresence mode="wait">
                  <motion.img
                    key={img}
                    initial={{ opacity: 0, scale: 0.97 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.4, ease: "easeOut" }}
                    src={img}
                    alt={view === "tech" ? `${p.name} dimension drawing` : `${p.name}${finish ? ` in ${finish}` : ""}`}
                    className="max-h-full w-auto max-w-full object-contain"
                    data-testid="product-main-image"
                  />
                </AnimatePresence>
              </div>
            )}
          </FadeUp>
          {thumbs.length > 1 && (
            <FadeUp y={16} delay={0.1} className="mt-5 flex flex-wrap gap-3" data-testid="product-thumbnails">
              {thumbs.map((t) => (
                <button
                  key={t.key}
                  type="button"
                  onClick={t.onClick}
                  aria-pressed={t.active}
                  data-testid={t.testId}
                  title={t.label}
                  className={`flex h-20 w-20 items-center justify-center overflow-hidden rounded-2xl bg-white p-2.5 transition duration-300 md:h-24 md:w-24 ${
                    t.active ? "ring-2 ring-ink" : "opacity-60 hover:opacity-100"
                  }`}
                >
                  <img src={t.src} alt={t.label} className="max-h-full w-auto object-contain" loading="lazy" />
                </button>
              ))}
            </FadeUp>
          )}
        </div>

        <div className="lg:col-span-5">
          <FadeUp y={16} delay={0.1}>
            <p className="text-[13px] font-semibold uppercase tracking-[0.2em] text-ink/50" data-testid="product-category">{p.category}</p>
            <h1 className="mt-3 font-display text-4xl font-medium leading-[1.08] tracking-tight text-ink md:text-5xl" data-testid="product-name">
              {p.name}
            </h1>
            {sku && (
              <p className="mt-3 text-sm font-medium tracking-wide text-ink/40" data-testid="product-sku">{sku}</p>
            )}
            {p.blurb && (
              <p className="mt-5 max-w-md text-base leading-relaxed text-ink/70">{p.blurb}</p>
            )}

            <dl className="mt-10 divide-y divide-ink/5 border-y border-ink/10" data-testid="product-specs">
              {specs.map((s) => (
                <div key={s.l} className="flex items-baseline justify-between gap-6 py-4">
                  <dt className="text-[12px] font-semibold uppercase tracking-[0.16em] text-ink/50">{s.l}</dt>
                  <dd className="text-right text-sm font-semibold text-ink">{s.v}</dd>
                </div>
              ))}
              {p.finishes.length > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-4 py-4" data-testid="spec-finishes-row">
                  <dt className="text-[12px] font-semibold uppercase tracking-[0.16em] text-ink/50">Available finishes</dt>
                  <dd className="flex flex-wrap justify-end gap-2">
                    {p.finishes.map((f) => (
                      <button
                        key={f}
                        type="button"
                        onClick={() => setFinish(f)}
                        data-testid={`spec-finish-${slug(f)}`}
                        className={`flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[12px] font-medium transition duration-300 ${
                          finish === f
                            ? "bg-ink text-white"
                            : "bg-white text-ink/70 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:text-ink"
                        }`}
                      >
                        <FinishSwatch finish={f} />
                        {f}
                      </button>
                    ))}
                  </dd>
                </div>
              )}
              {p.countertops && (
                <div className="flex flex-wrap items-center justify-between gap-4 py-4" data-testid="spec-benchtop-row">
                  <dt className="text-[12px] font-semibold uppercase tracking-[0.16em] text-ink/50">Benchtop</dt>
                  <dd className="flex flex-wrap justify-end gap-2">
                    {p.countertops.map((c) => (
                      <button
                        key={c}
                        type="button"
                        onClick={() => setCountertop(c)}
                        aria-pressed={countertop === c}
                        data-testid={`benchtop-${slug(c)}`}
                        className={`flex items-center gap-2 rounded-full px-3.5 py-1.5 text-[12px] font-medium transition duration-300 ${
                          countertop === c
                            ? "bg-ink text-white"
                            : "bg-white text-ink/70 shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:text-ink"
                        }`}
                      >
                        <FinishSwatch finish={c} />
                        {c}
                      </button>
                    ))}
                  </dd>
                </div>
              )}
            </dl>

            <Link
              to={`/contact?interest=${encodeURIComponent(p.category)}&product=${encodeURIComponent(enquiryProduct)}`}
              data-testid="product-enquire-btn"
              className="group mt-9 flex w-full items-center justify-center gap-2 rounded-full bg-ink px-8 py-4 text-[14px] font-semibold text-white transition duration-300 hover:scale-[1.01] hover:bg-ink/90"
            >
              Enquire about this {p.category === "Accessories" ? "piece" : "product"}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
            {!p.skuBase && (
              <a
                href={`/sheets/${p.id}.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="spec-sheet-download"
                className="group mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-ink/15 bg-white px-8 py-4 text-[14px] font-semibold text-ink transition duration-300 hover:border-ink hover:bg-ink hover:text-white"
              >
                <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                Download Product Catalogue
              </a>
            )}
            {(() => {
              const activePdf = (p.specPdfs && p.specPdfs[finish]) || (p.specPdf ? { url: p.specPdf, size: p.specPdfSize } : null);
              return activePdf ? (
                <a
                  href={assetUrl(activePdf.url)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="detailed-spec-download"
                  className="group mt-3 flex w-full items-center justify-center gap-2 rounded-full border border-ink/15 bg-white px-8 py-4 text-[14px] font-semibold text-ink transition duration-300 hover:border-ink hover:bg-ink hover:text-white"
                >
                  <Download className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
                  Detailed Spec Sheet
                  <span className="rounded-full bg-sand px-2.5 py-1 text-[10px] font-bold uppercase tracking-[0.1em] text-ink/50 transition-colors duration-300 group-hover:bg-white/15 group-hover:text-white/80">
                    PDF{activePdf.size ? ` · ${(activePdf.size / (1024 * 1024)).toFixed(1)} MB` : ""}
                  </span>
                </a>
              ) : null;
            })()}
            <p className="mt-4 text-center text-xs text-ink/40">
              Trade pricing and full specifications available on enquiry.
            </p>
          </FadeUp>
        </div>
      </div>

      {related.length > 0 && (
        <section className="mt-28" data-testid="related-products">
          <FadeUp>
            <h2 className="font-display text-3xl font-medium tracking-tight text-ink md:text-4xl">
              More {p.category.toLowerCase()}
            </h2>
          </FadeUp>
          <div className="mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {related.map((r, i) => (
              <FadeUp key={r.id} delay={i * 0.07}>
                <ProductCard p={r} />
              </FadeUp>
            ))}
          </div>
        </section>
      )}
    </main>
  );
};

export default ProductDetail;
